import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge, Button, EmptyState, InfoBox, LoadingState, kstDotDateStr } from '@biznomi/ui-react'
import {
  fileSizeText,
  getMyPayslips,
  openPayslipFile,
  payPeriodLabel,
  type MyPayslip,
} from '@/lib/api/payslip'

/**
 * 내 급여명세서 (직원).
 *
 * 회사가 급여 시스템에서 뽑은 PDF 를 받아 보는 화면. 금액에 대해 우리가 하는 말은
 * 하나도 없다 — 맞는지, 얼마인지, 공제가 옳은지 판단하지 않는다 (§1 경계).
 * 이 화면이 보증하는 건 "받은 파일이 보낸 그대로다" 뿐이다.
 */

function StatusBadge({ s }: { s: MyPayslip['status'] }) {
  if (s === 'VIEWED') return <Badge variant="outline">확인함</Badge>
  return <Badge variant="warning">새 명세서</Badge>
}

export default function Payslips() {
  const qc = useQueryClient()
  const [opening, setOpening] = useState<string | null>(null)
  const [error, setError] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['payslips', 'mine'],
    queryFn: getMyPayslips,
  })
  const rows = data?.success ? data.data ?? [] : []
  const unviewed = rows.filter((r) => r.status === 'SENT').length

  async function view(p: MyPayslip) {
    setOpening(p.id)
    setError('')
    const res = await openPayslipFile(p.id)
    setOpening(null)

    if ('error' in res) {
      setError(res.error)
      return
    }

    // 새 탭에서 연다. iOS 는 iframe 안의 PDF 를 자주 못 그리는데 blob 탭은 뜬다.
    const win = window.open(res.url, '_blank')
    if (!win) window.location.href = res.url
    qc.invalidateQueries({ queryKey: ['payslips', 'mine'] })
    qc.invalidateQueries({ queryKey: ['payslips', 'my-unviewed-count'] })
    setTimeout(() => URL.revokeObjectURL(res.url), 60_000)
  }

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold">급여명세서</h1>

      {unviewed > 0 && (
        <InfoBox tone="warning">아직 안 열어 본 명세서가 {unviewed}건 있습니다.</InfoBox>
      )}

      {error && (
        <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState
          title="받은 급여명세서가 없습니다"
          hint="회사에서 명세서를 올리면 여기에 뜹니다."
        />
      ) : (
        <ul className="space-y-3">
          {rows.map((p) => (
            <li key={p.id} className="space-y-3 rounded-card bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold">{payPeriodLabel(p.payPeriod)}</p>
                  <p className="mt-0.5 truncate text-xs text-slate-400">
                    {fileSizeText(p.fileSize)} · 받은 날 {kstDotDateStr(p.createdAt)}
                    {p.viewedAt ? ` · 확인 ${kstDotDateStr(p.viewedAt)}` : ''}
                  </p>
                </div>
                <StatusBadge s={p.status} />
              </div>

              <Button
                variant="secondary"
                className="w-full"
                loading={opening === p.id}
                onClick={() => void view(p)}
              >
                명세서 보기
              </Button>
            </li>
          ))}
        </ul>
      )}

      {rows.length > 0 && (
        /*
          금액에 대해 아무 말도 하지 않는다. 보증하는 건 파일이 안 바뀌었다는 것뿐이고,
          내용 문의는 회사로 보낸다 — 우리가 계산하지 않았으므로 설명할 수도 없다.
        */
        <p className="text-xs leading-relaxed text-slate-400">
          명세서는 회사에서 올린 파일 그대로입니다. 올린 뒤에 바뀌지 않았는지 저희가 확인해서
          보여 드립니다. 금액이 궁금하면 회사 담당자에게 물어보세요.
        </p>
      )}
    </div>
  )
}
